import React from 'react'
import { Container } from '../components/container'
import { css } from '@emotion/react'
import { InViewFadeUp } from '../components/in-view-fade'
import { useTheme } from '@conte-ltd/components-theme'
import { Theme } from '../styles/theme'
import { useMediaQuery } from '@conte-ltd/components-utils'
import { StaticImage } from 'gatsby-plugin-image'
import { Company } from '../components/company'
import { Layout } from '../components/layout'
import { ToContact } from '../components/to-contact'
import { Background, Parallax } from 'react-parallax'
import { Section } from '../components/section'

const AboutPage = () => {
  const {
    styles: {
      fontFamily,
      fontSize,
      fontWeight,
      lineHeight,
      position,
      top,
      right,
      margin,
      padding,
      width,
      height,
      color,
      border,
    },
    variables: { spacing },
    breakpoints: bp,
  } = useTheme<Theme>()
  const isMobile = useMediaQuery(bp.lg)
  const isDesktop = !isMobile

  return (
    <Layout>
      <Section
        css={color.white}
        className={isMobile ? 'dark-mode' : 'light-mode'}
      >
        <Container>
          <div
            css={[
              position.absolute,
              top.none,
              isDesktop
                ? css`
                    right: 20rem;
                  `
                : right.none,
              width.vwFull,
              height.full,
              isMobile &&
                css`
                  margin: 0 calc(50% - 50vw);
                `,
            ]}
          >
            <StaticImage
              src={'../images/subcut_06.jpg'}
              alt={'蒲郡の大楠'}
              css={[
                width.full,
                height.full,
                css`
                  filter: brightness(0.25);
                `,
              ]}
            />
          </div>

          <Container
            css={[
              position.relative,
              isDesktop
                ? [padding.top['5xl'], padding.bottom['4xl']]
                : [padding.top['4xl'], padding.bottom['3xl']],
            ]}
          >
            <h1
              css={[
                isMobile ? fontSize['4xl'] : fontSize['8xl'],
                lineHeight.normal,
              ]}
            >
              <InViewFadeUp>
                IMAGINATION
                <br />
                FIRST.
              </InViewFadeUp>
            </h1>

            <h2
              css={[
                fontFamily.jp,
                fontWeight.semibold,
                isMobile
                  ? [margin.top.sm, fontSize['2lg']]
                  : [margin.top.lg, fontSize['2xl']],
              ]}
            >
              <InViewFadeUp>自由に、創る</InViewFadeUp>
            </h2>

            <div css={margin.top.xl}>
              <p>
                <InViewFadeUp>
                  場所、時間、概念にとらわれない
                  <br />
                  自由な働き方から生まれる自由な発想。
                </InViewFadeUp>
                <br />
                <br />
                <InViewFadeUp>
                  多様な価値観に溢れた時代だからこそ、
                  {isDesktop && <br />}
                  私たちは自由をちからにして、
                  {isDesktop && <br />}
                  お客さまのビジネス・プロダクトを最大限に魅せるために
                  {isDesktop && <br />}
                  トータルでクリエイティブを提供する会社です。
                </InViewFadeUp>
                <br />
                <br />
                <br />
                <InViewFadeUp>
                  会社名の「CONTE」には、3つの想いがこめられています。
                </InViewFadeUp>
              </p>
            </div>

            <div
              css={[
                margin.top.md,
                padding.all.sm,
                border.all.default,
                css`
                  max-width: 64rem;

                  & > h3 {
                    ${[fontSize['3lg']]};

                    &:not(:first-of-type) {
                      ${margin.top.sm};
                    }
                  }

                  & > p {
                    ${[
                      margin.top['3xs'],
                      isMobile ? fontSize.xs : fontSize.sm,
                    ]};
                  }
                `,
              ]}
            >
              <h3>
                <InViewFadeUp>Con te</InViewFadeUp>
              </h3>
              <p>
                <InViewFadeUp>
                  イタリア語で“あなたと、“寄り添う”の意味。
                </InViewFadeUp>
              </p>

              <h3>
                <InViewFadeUp>Congruent</InViewFadeUp>
              </h3>
              <p>
                <InViewFadeUp>
                  常に等しいという意味の合同記号「≡」。
                  <br />
                  「思考」「発言」「行動」「感覚」4つの要素がフィットしており矛盾のない状態。
                </InViewFadeUp>
              </p>

              <h3>
                <InViewFadeUp>Continuity</InViewFadeUp>
              </h3>
              <p>
                <InViewFadeUp>
                  ”継続”や”連続”という意味。デザインや映像の台本となる「絵コンテ」の語源。
                </InViewFadeUp>
              </p>
            </div>

            <div css={margin.top.md}>
              <p>
                <InViewFadeUp>
                  私たちは、自分自身に矛盾のない状態で、やりたいことを自由に
                  {isDesktop && <br />}
                  パフォーマンスを最大限発揮できる会社を目指しています。
                </InViewFadeUp>
                <br />
                <br />
                <InViewFadeUp>
                  そこから生まれる自由な発想を生かして、
                  {isDesktop && <br />}
                  お客さまの想いに寄り添いながら、
                  {isDesktop && <br />}
                  継続してお付き合いいただけるクリエイティブパートナーとして
                  {isDesktop && <br />}
                  ビジネスやプロダクトの成長に貢献していきます。
                </InViewFadeUp>
              </p>
            </div>
          </Container>
        </Container>
      </Section>

      <Section
        className={'light-mode'}
        css={css`
          padding-bottom: 20rem;

          @media ${bp.lg} {
            padding-bottom: 10rem;
          }
        `}
      >
        <Container>
          <div css={isDesktop ? padding.y['4xl'] : padding.y.xl}>
            <h1
              css={[
                isMobile ? fontSize['4xl'] : fontSize['8xl'],
                lineHeight.normal,
              ]}
            >
              CREATIVE
              <br />
              PARTNER.
            </h1>

            <h2
              css={[
                isMobile ? margin.top.sm : margin.top.lg,
                isMobile ? fontSize['2lg'] : fontSize['2xl'],
              ]}
            >
              必要なのは、創れる相方です。
            </h2>

            <p css={margin.top.xl}>
              <InViewFadeUp>
                企業・サービス・商品、
                <br />
                生み出すものには強い想いがあるはず。
              </InViewFadeUp>
              <br />
              <br />
              <InViewFadeUp>
                CONTEはその「想い」をカタチにして、
                {isMobile && <br />}
                多くの人へ伝えるお手伝いをしています。
              </InViewFadeUp>
              <br />
              <br />
              <InViewFadeUp>
                どのようにカタチにするか、
                <br />
                どのように伝えるのか。
              </InViewFadeUp>
              <br />
              <br />
              <InViewFadeUp>
                「想いはあるけれど、どうしたらいいのかわからない」。
                <br />
                私たちは、そんなお客さまの気軽に相談できる創り手＝
                {isDesktop && <br />}
                「クリエイティブパートナー」でありたいと思っています。
              </InViewFadeUp>
              <br />
              <br />
              <InViewFadeUp>
                納品して終わりではなく、次の一手を見据えた頼れる相方として
                {isDesktop && <br />}
                一緒にゴールを目指していきます。
              </InViewFadeUp>
            </p>
          </div>

          <div
            css={css`
              position: absolute;
              top: 20rem;
              right: -24rem;
              width: 50%;
              display: flex;
              flex-direction: column;
              gap: ${spacing.xl};

              @media ${bp.lg} {
                position: relative;
                align-items: center;
                gap: ${spacing.lg};
                top: unset;
                right: unset;
                width: 100vw;
                margin: 0 calc(50% - 50vw);
              }
            `}
          >
            <Parallax
              strength={-100}
              css={css`
                width: 676px;
                height: 164px;

                @media ${bp.lg} {
                  width: 100%;
                  height: 130px;
                }

                @media ${bp.md} {
                  width: 100%;
                  height: 100px;
                }
              `}
            >
              <Background>
                <StaticImage
                  src="../images/subcut_01.jpg"
                  alt="蒲郡の大楠"
                  objectFit={'cover'}
                  css={css`
                    width: 812px;
                  `}
                />
              </Background>
            </Parallax>

            <Parallax
              strength={-100}
              css={css`
                width: 676px;
                height: 164px;

                @media ${bp.lg} {
                  width: 100%;
                  height: 130px;
                }

                @media ${bp.md} {
                  width: 100%;
                  height: 100px;
                }
              `}
            >
              <Background>
                <StaticImage
                  src="../images/subcut_02.jpg"
                  alt="蒲郡の海"
                  objectFit={'cover'}
                  css={css`
                    width: 812px;
                    margin-top: -20rem;
                  `}
                />
              </Background>
            </Parallax>

            <Parallax
              strength={-100}
              css={css`
                width: 676px;
                height: 164px;

                @media ${bp.lg} {
                  width: 100%;
                  height: 130px;
                }

                @media ${bp.md} {
                  width: 100%;
                  height: 100px;
                }
              `}
            >
              <Background>
                <StaticImage
                  src="../images/subcut_03.jpg"
                  alt="ガマフォルニア"
                  objectFit={'cover'}
                  css={css`
                    width: 812px;
                    margin-top: -20rem;

                    @media ${bp.lg} {
                      margin-top: -30rem;
                    }
                  `}
                />
              </Background>
            </Parallax>
          </div>
        </Container>

        <Container
          css={css`
            height: 66rem;

            @media ${bp.lg} {
              display: none;
            }
          `}
        >
          <StaticImage
            src={'../images/keyvisual_03.jpg'}
            alt={'CONTE　キービジュアル２'}
            css={[height.full]}
          />
        </Container>
      </Section>

      <Company />

      <ToContact />
    </Layout>
  )
}

export default AboutPage
